/**
 * Patient API 타입 및 Hooks
 * @MP/shared/api/hooks/patient에서 타입과 hooks를 모두 import할 수 있도록 함
 */

// 타입 re-export
export type { Patient } from '../generated/patient/model/patient';
export type { PatientCreateRequest } from '../generated/patient/model/patient-create-request';
export { PatientHandedness as Handedness } from '../generated/patient/model/patient-handedness';
export { PatientGender as Gender } from '../generated/patient/model/patient-gender';
export { ServiceType } from '../generated/patient/model/service-type';
export { AgreementType } from '../generated/patient/model/agreement-type';
export type { Agreement } from '../generated/patient/model/agreement';
export type { AgreementSpec } from '../generated/patient/model/agreement-spec';
export type { OrgConsentStatusRow } from '../generated/patient/model/org-consent-status-row';
export type { AgreementDispatch } from '../generated/patient/model/agreement-dispatch';

// Hooks 구현
import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// 중복 접수(더블클릭·연타) 방지: 발송/생성 mutation 은 진행 중이면 새 요청 안 보내고 진행중 Promise 반환.
import { useGuardedMutation as useMutation } from '../../hooks/useGuardedMutation';
import { API_CONFIG } from '../config/env';
import {
  Patient,
  PatientControllerApi,
  AgreementControllerApi,
  AgreementDispatch,
  AgreementSpec,
  AgreementType,
  OrgConsentStatusRow,
  PatientPage,
  PatientUpdateRequest,
  ServiceType,
  PatientCreateRequest,
} from '../generated/patient';
import {
  QK_DASHBOARD_AGREEMENT_DISPATCHES,
  QK_DASHBOARD_CONSENT_STATUS,
  QK_PATIENT_AGREEMENT_URL,
  QK_PATIENT_AGREEMENTS,
  QK_PATIENT_DETAIL,
  QK_PATIENT_LIST,
} from '../query-keys';

const PATIENT_URL = API_CONFIG.PATIENT_URL;
const patientControllerApi = new PatientControllerApi(undefined, PATIENT_URL);
const agreementControllerApi = new AgreementControllerApi(undefined, PATIENT_URL);

export const useFetchPatients = (searchText: string) => {
  return useInfiniteQuery<
    PatientPage,
    AxiosError,
    InfiniteData<Patient, Patient | undefined>,
    any,
    Patient | undefined
  >({
    queryKey: [QK_PATIENT_LIST, searchText],
    queryFn: ({ pageParam: lastUser }) =>
      patientControllerApi.getPatientPages(searchText, 500, lastUser?.id).then((res) => res.data),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      const users = lastPage.patients;
      if (users.length > 0 && !lastPage.isLast) {
        return users[users.length - 1];
      }
      return undefined;
    },
    select: (data) => {
      const temp: Patient[] = [];

      data.pages.forEach((page) => {
        temp.push(...page.patients);
      });

      return {
        pages: temp,
        pageParams: data.pageParams,
      };
    },
  });
};

export const useFetchPatient = (patientId?: string, enabled: boolean = true) => {
  return useQuery<Patient, AxiosError>({
    queryKey: [QK_PATIENT_DETAIL, patientId],
    queryFn: () => patientControllerApi.findById(patientId!!).then((res) => res.data),
    enabled: !!patientId && !!enabled,
  });
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation<Patient, AxiosError, { patientId: string; body: PatientUpdateRequest }>({
    mutationFn: ({ patientId, body }) => patientControllerApi.update(patientId, body).then((res) => res.data),
    onSuccess: (_, { patientId }) => {
      queryClient.invalidateQueries({ queryKey: [QK_PATIENT_DETAIL, patientId] });
      // 목록(대시보드 이름/성별 소스=useFetchPatients 포함)도 갱신. 없으면 본인이 수정해도
      // 대시보드/목록이 새로고침 전까지 이전 이름·성별을 보여준다(피드는 다른 PC용).
      queryClient.invalidateQueries({ queryKey: [QK_PATIENT_LIST] });
    },
  });
};

export const useDeletePatient = () => {
  const queryClient = useQueryClient();
  return useMutation<any, AxiosError, string>({
    mutationFn: async (patientId) => patientControllerApi._delete(patientId),
    onSuccess: (_, patientId) => {
      queryClient.invalidateQueries({ queryKey: [QK_PATIENT_DETAIL, patientId] });
      // 목록도 갱신. 없으면 삭제한 환자가 대시보드 로스터(byId)에 남아 그 환자의 행이 안 사라진다.
      queryClient.invalidateQueries({ queryKey: [QK_PATIENT_LIST] });
    },
  });
};

export const useCheckPatientCodeExists = () => {
  return useMutation<boolean, AxiosError, string>({
    mutationFn: async (code) => patientControllerApi.exist(code).then((res) => res.data),
  });
};

export const useCheckPatientPhoneExists = () => {
  return useMutation<boolean, AxiosError, string>({
    mutationFn: async (phoneNumber) => patientControllerApi.exist(undefined, phoneNumber).then((res) => res.data),
  });
};

// AgreementType 목록을 AgreementSpec[] 형태로 변환 (각 type 하나당 한 항목)
const toAgreementSpecs = (types?: AgreementType[]): AgreementSpec[] =>
  (types ?? []).map((type) => ({ type }));

export const useSendAgreement = () => {
  return useMutation<string, AxiosError, { patientId: string; types: AgreementType[] }>({
    mutationFn: ({ patientId, types }) =>
      agreementControllerApi.send(patientId, { agreements: toAgreementSpecs(types) }).then((res) => res.data),
  });
};

export const useFetchAgreementUrl = (patientId?: string, types?: AgreementType[], enabled: boolean = true) => {
  return useQuery<string, AxiosError>({
    queryKey: [QK_PATIENT_AGREEMENT_URL, patientId, types],
    queryFn: () =>
      agreementControllerApi.getUrl(patientId!!, { agreements: toAgreementSpecs(types) }).then((res) => res.data),
    enabled: !!patientId && !!types && types.length > 0 && enabled,
    gcTime: 0,
  });
};

/**
 * 발송 대상(types)이 렌더가 아닌 호출 시점에 정해지는 흐름(동의서 선택 후 전송 등)에서 쓰는 명령형 fetcher.
 * useFetchAgreementUrl은 types가 쿼리 키에 고정되므로, 대상을 인자로 넘겨 조회하는 경우 이 훅을 사용한다.
 */
export const useFetchAgreementUrlLazy = () => {
  const client = useQueryClient();
  return (patientId: string, types: AgreementType[]) =>
    client.fetchQuery({
      queryKey: [QK_PATIENT_AGREEMENT_URL, patientId, types],
      queryFn: () =>
        agreementControllerApi.getUrl(patientId, { agreements: toAgreementSpecs(types) }).then((res) => res.data),
      gcTime: 0,
    });
};

export const useFetchAgreements = (patientId?: string) => {
  return useQuery({
    queryKey: [QK_PATIENT_AGREEMENTS, patientId],
    queryFn: () =>
      agreementControllerApi.findPatientAgreements(patientId!!).then((res) => {
        return {
          patientId: patientId!!,
          agreements: res.data,
        };
      }),
    enabled: !!patientId,
    gcTime: 0,
  });
};

/**
 * patient-api org 단위 '오늘 동의완료' 현황(GET /api/v1/agreements/org-status).
 * feed.events(USER_AGREED 등) 수신 시 FeedSocketProvider 가 QK_DASHBOARD_CONSENT_STATUS 를 invalidate → 자동 refetch.
 */
export const useOrgConsentStatus = () =>
  useQuery<OrgConsentStatusRow[], AxiosError>({
    queryKey: [QK_DASHBOARD_CONSENT_STATUS],
    queryFn: () => agreementControllerApi.orgStatus().then((res) => res.data),
  });

/**
 * org 단위 동의서 발송 기록(GET /api/v1/agreements/dispatches).
 * 기본 응답이 '기한이 남아 있고 아직 동의하지 않은' 건이라 그대로 미동의 목록이 된다.
 * 기관은 토큰에서 읽으므로 지정할 수 없고, 종류를 지정하지 않으면 모든 종류가 섞여 나온다.
 * 환자명·차트번호는 실려 오지 않으므로 호출부가 환자 목록과 patientId 로 조합해야 한다.
 */
export const useFetchAgreementDispatches = (agreementType: AgreementType) =>
  useQuery<AgreementDispatch[], AxiosError>({
    queryKey: [QK_DASHBOARD_AGREEMENT_DISPATCHES, agreementType],
    queryFn: () => agreementControllerApi.findDispatches(agreementType).then((res) => res.data),
    // STIM 이용 현황은 항상 최신 발송 기록을 조회한다(캐시 미사용).
    gcTime: 0,
    staleTime: 0,
  });

export const useSendPatientInvitation = () => {
  return useMutation<void, AxiosError, PatientCreateRequest>({
    mutationFn: (body) => patientControllerApi.sendPatientCreationLink(body).then((res) => res.data),
  });
};
